import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { throwError, map, switchMap, tap, of, BehaviorSubject, Subject, takeUntil, take } from 'rxjs';
import { GraphQlQuery } from '@models/query.model';
import { User } from '@models/user.model';
import { Error } from '@models/graphql.model';
import { CookieService } from '@services/cookie/cookie.service';
import { Seller, SellerBillingInfo, SellerOptions } from '@models/seller.model';
import { MenuService } from '@services/menu/menu.service';
import { MultivendeService } from '@services/integrations/multivende/multivende.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private router = inject(Router);
  private cookieService = inject(CookieService);
  readonly #menuService = inject(MenuService);
  readonly #multivendeService = inject(MultivendeService);
  private headers = new HttpHeaders({'Content-Type': 'application/json'});
  private token: string | null = null;
  private user = new BehaviorSubject<User>({
    id: '',
    name: '',
    email: '',
    admin: false,
    sellers: []
  });
  private seller = new BehaviorSubject<{Seller: Seller}>({
    Seller: {
      _id: '',
      name: '',
      origin: '',
      options: {} as SellerOptions,
      billing_info: {} as SellerBillingInfo,
      contacts: []
    }
  });
  user$ = this.user.asObservable();
  activeSeller$ = this.seller.asObservable();

  logIn(oauthToken: string) {
    const query: GraphQlQuery = {
      query: `query Login($oauthToken: JWT!) { Login(oauthToken: $oauthToken) { token } }`,
      variables: {
        oauthToken
      }
    }
    return this.http.post<{errors?: Error[], data: { Login: {token: string}}}>(environment.mainApiUri, query)
      .pipe(
        switchMap((response) => response.data?.Login.token ? of(response.data.Login.token) : throwError(() => new Error(response.errors?.[0].message))),
        tap((token) => this.setToken(token)),
        switchMap((token) => this.getUserInfo(token)),
      );
  }

  private setToken(token: string) {
    this.token = token;
    this.cookieService.setCookieSession(token);
  }

  private getUserInfo(token: string) {
    const query: GraphQlQuery = {
      query: `query GetUserAndSeller { User { id name admin email } Sellers { _id name company origin } }`,
    }
    const headers = this.headers.append('Authorization', `Bearer ${token}`)
    return this.http.post<{errors?: Error[], data: { User: User, Sellers: Seller[] }}>(environment.mainApiUri, query, { headers })
      .pipe(
        map((response) => {
          return {
            name: response.data.User.name,
            id: response.data.User.id,
            email: response.data.User.email,
            admin: response.data.User.admin,
            sellers: response.data.Sellers
          }
        }),
        tap((user) => {
          this.user.next(user);
          if(this.cookieService.checkSeller()){
            this.setSeller(this.cookieService.recoverySeller());
          }
          else{
            this.setSeller(user.sellers[0]._id);
          }
        }),
      )
  }

  getSellerInfo(seller: string){
    const query: GraphQlQuery = {
      query: `query GetSeller($seller: ObjectID!) {
        Seller(seller: $seller) {
          _id
          name
          origin
        }
      }`,
      variables: {
        seller: seller
      }
    }
    return this.http.post<{errors?: Error[], data: { Seller: Seller }}>(environment.mainApiUri, query)
      .pipe(
        map((response) => response.data)
        /*
        tap((seller) => {
          if(seller.Seller.origin === 'Multivende'){
            this.#multivendeService.validatePolling(seller.Seller._id);
          }
        })*/
      );
  }

  setSeller(id: string){
    this.getSellerInfo(id).subscribe((seller) => {
      this.seller.next(seller);
    });
    this.getUser().pipe(
      tap((user) => this.#menuService.getMenu(user.admin, id, user.id)),
      take(1)
    ).subscribe();
    this.cookieService.setCookieSeller(id);
    if(!window.location.pathname.includes('integrations')){
      this.router.navigate(['']);
    }
  }

  logOut(){
    this.token = null;
    this.cookieService.deleteCookieSession();
    window.localStorage.clear();
    window.location.href = environment.oauth.logOutUri;
  }

  isLoggedIn() {
    return this.token !== null;
  }

  getUser() {
    return this.user$;
  }

  getSeller() {
    return this.activeSeller$;
  }

  getToken() {
    return this.token;
  }

  recoverySession() {
    const token = this.cookieService.recoveryToken();
    const seller = this.cookieService.recoverySeller();
    this.token = token;
    this.setSeller(seller);
    const ngUnsubscribe = new Subject<void>();
    this.getUserInfo(token)
      .pipe(takeUntil(ngUnsubscribe))
      .subscribe(() => {
        ngUnsubscribe.next();
        ngUnsubscribe.complete();
      });
  }
}
